/**
 * HTTP client for the A2 key-registry service (PUT/GET /keys/{keyRef}). GET resolves
 * a broadcast epoch key (so others' sealed messages open); PUT uploads OUR own epoch
 * key as a signed DAG-CBOR envelope (verify_envelope-compatible). Signing needs the
 * identity, injected post-construction via bindUploader (BeaconClient calls it).
 */
import { dagCborEncode, signEnvelope, type CborValue } from "@beacon/crypto";
import { stdB64Decode } from "./model.js";
import type { webcrypto } from "node:crypto";
import type { KeyRegistry } from "./epoch-keys.js";

type CryptoKey = webcrypto.CryptoKey;

export interface UploadSigner { did: string; signingKey: string; signingPrivate: CryptoKey; }

/** Signed DAG-CBOR PUT body: {identityId, keyRef, signingKey, key, sig}. Field-agnostic
 *  signingInput means the registry's verify_envelope(map, head) accepts it like any envelope. */
export async function buildKeyUploadBody(
  signer: UploadSigner,
  keyRef: string,
  key32: Uint8Array,
): Promise<Uint8Array> {
  // Already in CBOR form: 3 text fields + `key` as a raw Uint8Array (dagCborEncode
  // emits it as CBOR bytes). Not routed through toCborModel — that maps the
  // {$bytes} JSON form, and would flatten a raw Uint8Array to {}.
  const cbor: Record<string, CborValue> = {
    identityId: signer.did,
    key: key32,
    keyRef,
    signingKey: signer.signingKey,
  };
  const sig = await signEnvelope(cbor, signer.signingPrivate);
  return dagCborEncode({ ...cbor, sig });
}

export type KeyUploader = (keyRef: string, key32: Uint8Array) => Promise<Uint8Array>;

export class HttpKeyRegistry implements KeyRegistry {
  readonly #base: string;
  readonly #fetch: typeof fetch;
  readonly #cache = new Map<string, Uint8Array>();
  #upload?: KeyUploader;
  #heal?: () => Promise<void>;

  constructor(opts: { baseUrl: string; fetchImpl?: typeof fetch }) {
    this.#base = opts.baseUrl.replace(/\/$/, "");
    this.#fetch = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /** Bind the signer that builds the signed PUT body (BeaconClient.create calls this). */
  bindUploader(upload: KeyUploader): void { this.#upload = upload; }

  /** Bind the genesis self-heal run on an `unknown-identity` upload reject (BeaconClient.create). */
  bindHealer(heal: () => Promise<void>): void { this.#heal = heal; }

  async get(keyRef: string): Promise<Uint8Array | null> {
    const c = this.#cache.get(keyRef);
    if (c) return c.slice();
    let res: Response;
    // keyRef is `did:...:epoch`; `:` is path-safe — send raw (no encodeURIComponent).
    try { res = await this.#fetch(`${this.#base}/${keyRef}`); }
    catch { return null; } // transient/unreachable → locked placeholder, never crash the feed
    if (!res.ok) return null; // 404 unknown / shredded
    const { key } = (await res.json()) as { key: string };
    const k = stdB64Decode(key);
    this.#cache.set(keyRef, k);
    return k.slice();
  }

  async put(keyRef: string, key32: Uint8Array): Promise<void> {
    if (key32.length !== 32) throw new Error("registry: key must be 32 bytes");
    if (!this.#upload) throw new Error("HttpKeyRegistry: no uploader bound (bindUploader first)");
    const body = await this.#upload(keyRef, key32);
    let res = await this.#putOnce(keyRef, body);
    // The registry only knows identities whose chain it has folded from the relay.
    // A wiped relay (redeploy) + a persisted bundle ⇒ `unknown-identity`: our epoch
    // key would be rejected forever, and the upload happens BEFORE any message
    // publish, so publish()'s own genesis self-heal never gets a chance to run.
    // Re-emit genesis via the bound healer, then retry once — the registry folds
    // the relay to its tail on every PUT, so the retry now resolves us. Scoped to
    // unknown-identity so a stale-key/bad-sig surfaces unretried (no loop).
    if (!res.ok && this.#heal && (await errorReason(res)) === "unknown-identity") {
      await this.#heal();
      res = await this.#putOnce(keyRef, body);
    }
    // 200 {stored:true|false} both = success (first-write-wins no-op); EpochKeys re-GETs
    // the canonical key after put. Any non-2xx is a real failure.
    if (!res.ok) throw new Error(`key-registry PUT ${keyRef} -> ${res.status}`);
  }

  #putOnce(keyRef: string, body: Uint8Array): Promise<Response> {
    return this.#fetch(`${this.#base}/${keyRef}`, {
      method: "PUT", headers: { "content-type": "application/octet-stream" }, body,
    });
  }
}

/** The relay/registry error contract is `{"error":{"reason","message"}}`; pull the
 *  reason to decide whether a reject is self-healable. Best-effort (never throws). */
async function errorReason(res: Response): Promise<string | undefined> {
  try {
    const j = (await res.json()) as { error?: { reason?: string } };
    return j.error?.reason;
  } catch {
    return undefined;
  }
}
